import React, { createContext, MutableRefObject, PropsWithChildren, useContext } from 'react';
import { DealBuilderContactsData, DealBuilderEquipmentSource, DealBuilderCreditApplicationSource } from '../types';
import { DealBuilderEntity, InventoryEquipmentItem, StartDealBuilderContactInput } from '../api';
import { Nullable } from 'global';
import { EquipmentDelivery } from 'modules/equipment/types';
import { useEquipmentActions } from 'modules/equipment';
import { useInvoice } from 'modules/invoice';
import { CalculatedTotalsByLevel } from 'modules/quote';
import { VendorOpportunity } from 'modules/vendorOpportunity';

type EquipmentUpdateDTO = Parameters<ReturnType<typeof useEquipmentActions>['onUpdateEquipment']>[0];
type InvoiceUpdateInput = Parameters<ReturnType<typeof useInvoice>['handleUpdateInvoice']>[0];

export type DeliverySubmitRef = MutableRefObject<
  Nullable<{ submit: () => Promise<{ isValid: boolean; formValues: EquipmentDelivery }> }>
>;

export interface DealBuilderContextValue {
  vo?: Nullable<VendorOpportunity>;
  creditApplication?: Nullable<DealBuilderCreditApplicationSource>;
  contactsData: DealBuilderContactsData;
  contactsLoading: boolean;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
  selectedEquipmentItem: Nullable<InventoryEquipmentItem>;
  setSelectedEquipmentItem: (item: Nullable<InventoryEquipmentItem>) => void;
  deliverySubmitRef: DeliverySubmitRef;
  activeEntity: Nullable<DealBuilderEntity>;
  historyEntities: DealBuilderEntity[];
  selectSavedProposal: (proposalId: string) => void;
  ensureDealBuilder: (contacts: StartDealBuilderContactInput[]) => Promise<Nullable<string>>;
  startNewQuote: () => Promise<Nullable<string>>;
  upsertContacts: (ownerPgIds: string[], accountContactIds: string[]) => void;
  upsertEquipments: (equipmentIds: string[], overrideProposalId?: string) => void;
  updateQuote: (input: Record<string, unknown>) => void;
  updateEquipments: (dtos: EquipmentUpdateDTO[]) => void;
  updateInvoice: (input: InvoiceUpdateInput) => void;
  setActiveEntity: (entity: DealBuilderEntity) => void;
  calculatedTotals: CalculatedTotalsByLevel;
  setCalculatedTotals: (totals: CalculatedTotalsByLevel) => void;
}

const DealBuilderContext = createContext<DealBuilderContextValue | null>(null);

export const DealBuilderContextProvider = ({
  value,
  children,
}: PropsWithChildren<{ value: DealBuilderContextValue }>) => (
  <DealBuilderContext.Provider value={value}>{children}</DealBuilderContext.Provider>
);

export const useDealBuilderContext = (): DealBuilderContextValue => {
  const context = useContext(DealBuilderContext);
  if (!context) throw new Error('useDealBuilderContext must be used within a DealBuilderContextProvider');
  return context;
};
